import { useQuery } from "@tanstack/react-query";
import { useParams, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Flight } from "@/types/flight";
import { FlightPageHeader } from "@/components/FlightPageHeader";
import { FlightTabs } from "@/components/FlightTabs";
import { SEOHead } from "@/components/SEOHead";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { startOfDay, addDays, endOfDay } from "date-fns";
import { useMemo, useState } from "react";

export default function OrganizationFlightsPage() {
  const { orgCode } = useParams<{ orgCode: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  
  // Fetch organization by code
  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', orgCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .ilike('code', orgCode || '')
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!orgCode,
  });

  // Fetch flights for this organization
  const { data: flights = [], isLoading: flightsLoading } = useQuery({
    queryKey: ['organization-flights', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      
      const today = startOfDay(new Date());
      const dayAfterTomorrow = startOfDay(addDays(new Date(), 2));
      
      const { data, error } = await supabase
        .from('flights')
        .select(`
          *,
          organization:organizations(*),
          departure_city:cities!flights_departure_city_id_fkey(*),
          arrival_city:cities!flights_arrival_city_id_fkey(*),
          aircraft_type:aircraft_types(*)
        `)
        .eq('organization_id', organization.id)
        .gte('scheduled_departure', today.toISOString())
        .lt('scheduled_departure', dayAfterTomorrow.toISOString())
        .order('scheduled_departure', { ascending: true });

      if (error) throw error;
      return data as Flight[];
    },
    enabled: !!organization?.id,
    staleTime: 30000,
  });

  // Filter flights for today and tomorrow separately
  const { todayFlights, tomorrowFlights } = useMemo(() => {
    const today = startOfDay(new Date());
    const tomorrow = startOfDay(addDays(new Date(), 1));
    const endOfTomorrow = endOfDay(tomorrow);

    const todayList = flights.filter(flight => {
      const departureDate = new Date(flight.scheduled_departure);
      return departureDate >= today && departureDate < tomorrow;
    });

    const tomorrowList = flights.filter(flight => {
      const departureDate = new Date(flight.scheduled_departure);
      return departureDate >= tomorrow && departureDate <= endOfTomorrow;
    });

    return { todayFlights: todayList, tomorrowFlights: tomorrowList };
  }, [flights]);

  // Loading state
  if (orgLoading || flightsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Organization not found
  if (!organization) {
    return <Navigate to="/not-found" replace />;
  }

  const pageTitle = `${organization.name} Flight Schedule - Today & Tomorrow`;
  const metaDescription = `View ${organization.name} flight schedules for today and tomorrow. Real-time flight status, departure times, arrival times, and gate information.`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={metaDescription}
        keywords={`${organization.name}, ${organization.code}, flights, schedule, today, tomorrow, Papua New Guinea`}
      />

      <div className="container mx-auto px-4 py-8 space-y-6">
        <AnnouncementBanner />
        
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold mb-2">{organization.name} Flight Schedule</h1>
          <p className="text-muted-foreground text-lg">Today & Tomorrow's Flights</p>
        </div>

        <FlightPageHeader 
          flightCount={flights.length}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {flights.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <h3 className="text-xl font-semibold mb-2">No Flights Scheduled</h3>
            <p className="text-muted-foreground">
              There are no flights scheduled for {organization.name} today or tomorrow.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {todayFlights.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Today's Flights</h2>
                <FlightTabs 
                  todaysFlights={todayFlights}
                  allFlights={todayFlights}
                />
              </section>
            )}

            {tomorrowFlights.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Tomorrow's Flights</h2>
                <FlightTabs 
                  todaysFlights={tomorrowFlights}
                  allFlights={tomorrowFlights}
                />
              </section>
            )}
          </div>
        )}

        <footer className="text-center text-sm text-muted-foreground mt-8">
          Last updated: {new Date().toLocaleString()}
        </footer>
      </div>
    </>
  );
}
